import type { GetServerSidePropsContext } from 'next';
import { useQueries, useQuery } from '@tanstack/react-query';

import { useLocale } from 'hooks';
import { $fetch } from 'utils/request';
import { PRODUCT_GET_ORDER_INFO } from 'utils/routes';
import type { TOrder } from 'types/order';
import { isBlank } from 'utils/StringUtils';
import { queryKeys } from 'utils/constants/reactQuery';

export type GetOrderInfoProps = {
  locale: string;
  email: string;
  locator: string;
  offlineReference?: string;
  store?: string;
  fiscalYear?: string;
  includeReturnLabel?: boolean;
  context?: GetServerSidePropsContext;
};

export async function getOfflineOrderInfo({ locale, offlineReference, store, fiscalYear, includeReturnLabel }: GetOrderInfoProps) {
  return await $fetch<TOrder>(
    { path: PRODUCT_GET_ORDER_INFO },
    {
      method: 'POST',
      body: JSON.stringify({
        profile: locale,
        offlineReference,
        store,
        fiscalYear,
        includeReturnLabel,
      }),
    },
  );
}

export async function getOrderInfoV2({ locale, email, locator, includeReturnLabel }: GetOrderInfoProps) {
  return await $fetch<TOrder>(
    { path: PRODUCT_GET_ORDER_INFO },
    {
      method: 'POST',
      body: JSON.stringify({
        profile: locale,
        email,
        locator,
        includeReturnLabel,
      }),
    },
  );
}

export function useOrder({ offlineReference, locator, email, ...rest }: Omit<GetOrderInfoProps, 'context' | 'locale'>) {
  const { locale } = useLocale();
  const isOffline = !isBlank(offlineReference);
  const orderReference = isOffline ? offlineReference : locator;
  return useQuery({
    queryKey: [queryKeys.order, orderReference],
    queryFn: async () =>
      isOffline ? getOfflineOrderInfo({ locale, offlineReference, email, locator, ...rest }) : getOrderInfoV2({ locale, email, locator, ...rest }),
  });
}

export function useOrderSiblings({ email, locators = [], ...rest }: Omit<GetOrderInfoProps, 'context' | 'locale' | 'locator'> & { locators?: string[] }) {
  const { locale } = useLocale();
  return useQueries({
    queries: locators.map((locator) => ({
      queryKey: [queryKeys.order, locator],
      queryFn: async () => getOrderInfoV2({ locale, email, locator, ...rest }),
    })),
    combine: (results) => {
      return {
        siblings: results.reduce((acc, r) => {
          if (r.status === 'success' && r.data) {
            acc.push(r.data);
          }
          return acc;
        }, [] as TOrder[]),
        isPending: results.some((r) => r.isPending),
      };
    },
  });
}
